import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Header } from './components/Header';
import { PollCreator } from './components/PollCreator';
import { FindPoll } from './components/FindPoll';
import { SharePoll } from './components/SharePoll/SharePoll';
import { VotingPage } from './components/VotingPage';
import { Results } from './components/Results';
import { EditPoll } from './components/EditPoll/EditPoll';
import { Toast } from './components/Toast/Toast';
import { useToast } from './hooks/useToast';
import { supabase } from './lib/supabase';
import { generatePollCode } from './utils/pollUtils';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from './hooks/useAuth';
import { getCreatedPolls, getVotedPolls, markPollAsCreated } from './utils/anonymousId';

const POLLS_PER_PAGE = 10;

function Home() {
  const { toast, showToast, hideToast } = useToast();
  const { userId } = useAuth();
  const [showCreatedPolls, setShowCreatedPolls] = useState(true);
  const [showVotedPolls, setShowVotedPolls] = useState(true);
  const [createdPollsPage, setCreatedPollsPage] = useState(1);
  const [votedPollsPage, setVotedPollsPage] = useState(1);
  const [isCreatedPollsPaginated, setIsCreatedPollsPaginated] = useState(false);
  const [isVotedPollsPaginated, setIsVotedPollsPaginated] = useState(false);

  const handleFindPoll = async (pollCode: string) => {
    try {
      const normalizedCode = pollCode.toLowerCase().trim();
      const { data: poll, error } = await supabase
        .from('polls')
        .select('id')
        .eq('id', normalizedCode);

      if (error) throw error;
      
      if (poll && poll.length > 0) {
        window.location.href = `/${poll[0].id}`;
      } else {
        showToast('Poll not found. Please check the code and try again.');
      }
    } catch (error) {
      console.error('Error finding poll:', error);
      showToast('Failed to find poll. Please try again.');
    }
  };

  const handleCreatePoll = async (
    pollName: string, 
    candidates: string[], 
    settings: {
      allowNominations: boolean;
      showSharingIcons: boolean;
      singleUpvoteOnly: boolean;
      duration?: number;
      expiresAt?: string;
    }
  ) => {
    try {
      const pollId = generatePollCode();
      
      const { data: poll, error: pollError } = await supabase
        .from('polls')
        .insert({
          id: pollId,
          name: pollName,
          author_id: userId,
          settings: {
            allowNominations: settings.allowNominations,
            showSharingIcons: settings.showSharingIcons,
            singleUpvoteOnly: settings.singleUpvoteOnly,
            isClosed: false
          },
          expires_at: settings.expiresAt
        })
        .select()
        .single();

      if (pollError) {
        console.error('Poll creation error:', pollError);
        throw new Error('Failed to create poll');
      }

      if (!poll) {
        throw new Error('Poll creation failed - no poll returned');
      }

      const candidateRecords = candidates
        .filter(name => name.trim())
        .map(name => ({
          id: uuidv4(),
          poll_id: pollId,
          name: name.trim()
        }));

      if (candidateRecords.length === 0) {
        throw new Error('No valid candidates provided');
      }

      const { error: candidatesError } = await supabase
        .from('candidates')
        .insert(candidateRecords);

      if (candidatesError) {
        console.error('Candidates creation error:', candidatesError);
        await supabase.from('polls').delete().eq('id', pollId);
        throw new Error('Failed to create candidates');
      }

      markPollAsCreated(pollId, pollName);
      window.location.href = `/share/${pollId}`;
    } catch (error) {
      console.error('Error creating poll:', error);
      showToast(error instanceof Error ? error.message : 'Failed to create poll. Please try again.');
    }
  };

  const createdPolls = getCreatedPolls().sort((a, b) => b.timestamp - a.timestamp);
  const votedPolls = getVotedPolls().sort((a, b) => b.timestamp - a.timestamp);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleLoadMoreCreatedPolls = () => {
    if (createdPollsPage * POLLS_PER_PAGE >= createdPolls.length) {
      setIsCreatedPollsPaginated(true);
    } else {
      setCreatedPollsPage(p => p + 1);
    }
  };

  const handleLoadMoreVotedPolls = () => {
    if (votedPollsPage * POLLS_PER_PAGE >= votedPolls.length) {
      setIsVotedPollsPaginated(true);
    } else {
      setVotedPollsPage(p => p + 1);
    }
  };

  const handleCollapseCreatedPolls = () => {
    setShowCreatedPolls(false);
    setCreatedPollsPage(1);
    setIsCreatedPollsPaginated(false);
  };

  const handleCollapseVotedPolls = () => {
    setShowVotedPolls(false);
    setVotedPollsPage(1);
    setIsVotedPollsPaginated(false);
  };

  const visibleCreatedPolls = isCreatedPollsPaginated 
    ? createdPolls.slice((createdPollsPage - 1) * POLLS_PER_PAGE, createdPollsPage * POLLS_PER_PAGE)
    : createdPolls.slice(0, createdPollsPage * POLLS_PER_PAGE);

  const visibleVotedPolls = isVotedPollsPaginated
    ? votedPolls.slice((votedPollsPage - 1) * POLLS_PER_PAGE, votedPollsPage * POLLS_PER_PAGE)
    : votedPolls.slice(0, votedPollsPage * POLLS_PER_PAGE);

  const hasMoreCreatedPolls = createdPolls.length > visibleCreatedPolls.length;
  const hasMoreVotedPolls = votedPolls.length > visibleVotedPolls.length;

  const totalCreatedPages = Math.ceil(createdPolls.length / POLLS_PER_PAGE);
  const totalVotedPages = Math.ceil(votedPolls.length / POLLS_PER_PAGE);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center gap-8">
          <div className="w-full max-w-md">
            {/* Vote in a Poll Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
              <h2 className="text-2xl font-bold text-center mb-6 dark:text-white">
                Vote in a Poll
              </h2>
              <FindPoll onFind={handleFindPoll} />
            </div>

            {/* Create a Poll Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-8">
              <PollCreator onCreatePoll={handleCreatePoll} />
            </div>

            {/* My Polls Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-4">
              <button
                onClick={() => setShowCreatedPolls(!showCreatedPolls)}
                className="w-full flex items-center justify-between text-left"
              >
                <h2 className="text-xl font-bold dark:text-white">My Polls</h2>
                {showCreatedPolls ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </button>
              {showCreatedPolls && (
                <div className="mt-4 space-y-2">
                  {createdPolls.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-center">You haven't created any polls yet</p>
                  ) : (
                    <>
                      {visibleCreatedPolls.map(poll => (
                        <a
                          key={poll.pollId}
                          href={`/${poll.pollId}/results`}
                          className="block p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-medium dark:text-white">{poll.pollName}</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {formatDate(poll.timestamp)}
                            </span>
                          </div>
                        </a>
                      ))}
                      {hasMoreCreatedPolls && !isCreatedPollsPaginated && (
                        <button
                          onClick={handleLoadMoreCreatedPolls}
                          className="w-full text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 py-2"
                        >
                          Show More
                        </button>
                      )}
                      {isCreatedPollsPaginated && (
                        <div className="flex justify-center gap-2 mt-4">
                          {Array.from({ length: totalCreatedPages }, (_, i) => (
                            <button
                              key={i}
                              onClick={() => setCreatedPollsPage(i + 1)}
                              className={`px-3 py-1 rounded ${
                                createdPollsPage === i + 1
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                              }`}
                            >
                              {i + 1}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* My Votes Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <button
                onClick={() => setShowVotedPolls(!showVotedPolls)}
                className="w-full flex items-center justify-between text-left"
              >
                <h2 className="text-xl font-bold dark:text-white">My Votes</h2>
                {showVotedPolls ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </button>
              {showVotedPolls && (
                <div className="mt-4 space-y-2">
                  {votedPolls.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-center">You haven't voted in any polls yet</p>
                  ) : (
                    <>
                      {visibleVotedPolls.map(poll => (
                        <a
                          key={poll.pollId}
                          href={`/${poll.pollId}/results`}
                          className="block p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-medium dark:text-white">{poll.pollName}</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {formatDate(poll.timestamp)}
                            </span>
                          </div>
                        </a>
                      ))}
                      {hasMoreVotedPolls && !isVotedPollsPaginated && (
                        <button
                          onClick={handleLoadMoreVotedPolls}
                          className="w-full text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 py-2"
                        >
                          Show More
                        </button>
                      )}
                      {isVotedPollsPaginated && (
                        <div className="flex justify-center gap-2 mt-4">
                          {Array.from({ length: totalVotedPages }, (_, i) => (
                            <button
                              key={i}
                              onClick={() => setVotedPollsPage(i + 1)}
                              className={`px-3 py-1 rounded ${
                                votedPollsPage === i + 1
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                              }`}
                            >
                              {i + 1}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      {toast && <Toast message={toast} onDismiss={hideToast} />}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/share/:pollId" element={<SharePoll />} />
        <Route path="/:pollId" element={<VotingPage />} />
        <Route path="/:pollId/results" element={<Results />} />
        <Route path="/:pollId/edit" element={<EditPoll />} />
      </Routes>
    </BrowserRouter>
  );
}